import React, { Component } from 'react';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { package as PackagePropType } from 'lib/CustomPropTypes';

import buildUrl from 'lib/buildUrl';

import { getVerticalName, getVerticalModuleClassName } from 'lib/verticalType';

import LazyLoad from 'components/LazyLoad';
import Link from 'components/Link';
import Picture from 'components/Picture';
import TypeIcon from 'components/TypeIcon';
import TeasePicture from 'components/TeasePicture';
import Unibrow from 'components/Unibrow';
import TitleLogo from 'components/TitleLogo';
import AIMS_FLAVORS from 'lib/aimsFlavors';

function hasFeaturedAuthor(article) {
  const { item, type } = article;

  if (
    type === 'article'
    && item && item.authors && item.authors[0]
    && item.authors[0].featuredAuthor.isFeaturedAuthor
  ) {
    return true;
  }

  return false;
}

function featuredAuthor(article, articleModuleClassName) {
  const { item } = article;
  const name = (item && item.authors[0] && item.authors[0].person && item.authors[0].person.name) || '';

  return (
    <span className={articleModuleClassName}>
      <span className="collection__author">{name}</span>
    </span>
  );
}

class Collection extends Component {
  static propTypes = {
    content: PackagePropType.isRequired,
    narrow: PropTypes.bool,
    pkgClassName: PropTypes.string,
  };

  static defaultProps = {
    narrow: false,
    pkgClassName: 'pkg collection',
  };

  static getArticleHeading({ article, verticalModuleStyle }) {
    if (!(verticalModuleStyle && verticalModuleStyle.toLowerCase() === 'think')) {
      const { computedValues: { unibrow } } = article;
      const primaryVertical = getVerticalName(article);
      const eyebrowToolkitClasses = ['f2', 'lh-none', 'founders-mono', 'fw1', 'ttu', 'mt0', 'mb1'];

      return (
        <Unibrow
          size="h4"
          className={classNames(
            {
              think: primaryVertical === 'think',
            },
            'collection__eyebrow',
            eyebrowToolkitClasses,
          )}
          unibrow={unibrow}
        />
      );
    }

    return null;
  }

  getBackgroundSizes() {
    const { narrow } = this.props;

    if (!narrow) {
      return {
        s: AIMS_FLAVORS.FOCAL_320x320, // Needs to be 760x760
        m: AIMS_FLAVORS.FOCAL_920x460,
        l: AIMS_FLAVORS.FOCAL_520x260,
        xl: AIMS_FLAVORS.FOCAL_760x380,
      };
    }

    return {
      s: AIMS_FLAVORS.FOCAL_320x320, // Needs to be 760x760
      l: AIMS_FLAVORS.FOCAL_520x260,
      xl: AIMS_FLAVORS.FOCAL_320x320, // Needs to be 760x760
    };
  }

  getThumbnailSizes(index) {
    const { narrow } = this.props;

    if (!narrow && index === 0) {
      return {
        s: AIMS_FLAVORS.FOCAL_80x80,
        m: AIMS_FLAVORS.FOCAL_320x160,
        l: AIMS_FLAVORS.FOCAL_160x160,
        xl: AIMS_FLAVORS.FOCAL_360x180,
      };
    }

    return {
      s: AIMS_FLAVORS.FOCAL_80x80,
    };
  }

  getArticleImage(article, index, articleModuleClassName) {
    const sizes = this.getThumbnailSizes(index);
    const { computedValues } = article;
    const primaryVertical = getVerticalName(article);

    if (hasFeaturedAuthor(article)) {
      return (
        <span
          className={articleModuleClassName}
        >
          <span
            className={classNames(
              {
                think: primaryVertical === 'think',
              },
              'icon',
              'icon-quote-mark',
              'collection__quote',
            )}
          />
        </span>
      );
    }

    return (
      <div className="collection__picture">
        <Link to={buildUrl(article)}>
          <TeasePicture
            responsiveFlavors={sizes}
            type={article.type}
            computedValues={computedValues}
            placeholder
            placeholderColor="white"
          />
          <TypeIcon type={article.type} className="collection__icon" />
        </Link>
      </div>
    );
  }

  article({
    item: article, index, verticalModuleStyle, articleModuleClassName,
  }) {
    if (!article) {
      return null;
    }

    const { computedValues } = article;
    if (!computedValues) {
      return null;
    }

    const hasAuthor = hasFeaturedAuthor(article);
    const classes = classNames('collection__article', { 'collection__article--hasFeaturedAuthor': hasAuthor });

    return (
      <li key={article.id} className={classes} data-contentid={article.id}>
        {this.getArticleImage(article, index, articleModuleClassName)}
        <div className="collection__info">
          {Collection.getArticleHeading(
            { article, verticalModuleStyle },
          )}
          <h3 className={classNames('collection__title')}>
            <Link to={buildUrl(article)}>
              {hasAuthor && featuredAuthor(article, articleModuleClassName)}
              {computedValues.headline}
            </Link>
          </h3>
        </div>
      </li>
    );
  }

  heading() {
    const {
      content: {
        metadata: {
          collectionName,
          collectionUrl,
          logoUrl,
        },
      },
    } = this.props;

    const headingModule = <TitleLogo logoUrl={logoUrl} title={collectionName} />;

    if (collectionUrl) {
      return <Link to={collectionUrl}>{headingModule}</Link>;
    }
    return headingModule;
  }

  background() {
    const {
      content: {
        metadata: {
          collectionImage,
          collectionUrl,
        },
      },
    } = this.props;

    const picture = (
      <LazyLoad>
        <Picture
          url={collectionImage}
          responsiveFlavors={this.getBackgroundSizes()}
          className="collection__background"
          placeholder
        />
      </LazyLoad>
    );

    if (collectionUrl) {
      return (
        <Link to={collectionUrl}>
          {picture}
        </Link>
      );
    }

    return picture;
  }

  render() {
    const {
      content: {
        items, metadata = {}, autofill, id,
      }, narrow, pkgClassName,
    } = this.props;

    let verticalModuleStyle;
    if (autofill && autofill.values) {
      const firstValue = autofill.values[0];
      // pulls the vertical from the `autofill.value`
      // `value` could be something like:
      // - "nbcnews/vertical/mach"
      // - "web/mach/"
      // (see here: https://regexr.com/3rdhs)
      [, verticalModuleStyle] = (/(?:\w+\/)?(?:vertical\/)?([\w-]+)(?:\/\w*)?/i.exec(firstValue) || []);
    }
    const classes = classNames(
      pkgClassName,
      'collection__container',
      { collection__wide: !narrow },
      getVerticalModuleClassName(verticalModuleStyle),
    );
    return (
      <section data-packageid={id} className={classes}>
        <header className="collection__header">
          <div className="collection__placeholder" />
          <div className="collection__headings">
            <h2 className="collection__heading">{this.heading()}</h2>
            <h3 className={classNames('collection__subheading', 'tc')}>{metadata.collectionDescription}</h3>
          </div>
          {this.background()}
        </header>

        <ul className="collection__articles">
          {items.map((item, index) => this.article(
            {
              item,
              index,
              verticalModuleStyle,
              articleModuleClassName: getVerticalModuleClassName(getVerticalName(item)),
            },
          ))}
        </ul>

        {metadata.collectionUrl && (
          <div className="collection__more">
            <Link to={metadata.collectionUrl}>
              {i18next.t('More')}
            </Link>
          </div>
        )}
      </section>
    );
  }
}
export default Collection;
